import { Link } from "@inertiajs/react";
import { Download, Users } from "lucide-react";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import { MarkdownContent } from "@/components/MarkdownContent";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getIcon } from "@/lib/icons";
import {
  bizminer_app_pillar_insight_improvement_levers_path,
  scoring_bizminer_app_pillar_insight_path,
} from "@/routes";
import type {
  BizminerPeerData,
  BizminerPillarInsight,
} from "@/types/serializers";
import { RadarChart } from "./_components/RadarChart";

interface Props {
  pillar_insight: BizminerPillarInsight;
  peer_data: BizminerPeerData;
}

function ExecutiveSummaryCard({
  executiveSummary,
}: {
  executiveSummary: string | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Executive Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <MarkdownContent
          content={executiveSummary || "Executive summary will be available soon."}
        />
      </CardContent>
    </Card>
  );
}

function TotalScoreCard({
  analysis,
  pillarInsight,
  peerData,
}: {
  analysis: BizminerPillarInsight["analysis"];
  pillarInsight: BizminerPillarInsight;
  peerData: BizminerPeerData;
}) {
  const hasPeers =
    peerData.peer_names.length > 0 && peerData.categories_with_peers.length > 0;
  const chartCategories = hasPeers
    ? peerData.categories_with_peers
    : analysis.categories;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle>Total Score</CardTitle>
        <span className="text-4xl font-bold">{analysis.account_score}</span>
      </CardHeader>
      <CardContent className="pb-6">
        <RadarChart
          accountName={analysis.account_name}
          competitorNames={hasPeers ? peerData.peer_names : []}
          categories={chartCategories}
          wrapperStyleLegend={{ bottom: 0 }}
          height={350}
          outerRadius="75%"
          showLegend={hasPeers}
        />
      </CardContent>
      <CardFooter>
        <Link href={scoring_bizminer_app_pillar_insight_path(pillarInsight.id)}>
          <Button className="w-full sm:w-auto">Scoring & Assessment</Button>
        </Link>
      </CardFooter>
    </Card>
  );
}

function ImprovementsCard({
  pillarInsight,
}: {
  pillarInsight: BizminerPillarInsight;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Improvements</CardTitle>
        <span className="text-4xl font-bold">
          {pillarInsight.improvement_lever_count}
        </span>
      </CardHeader>
      <CardContent className="space-y-4">
        {pillarInsight.top_improvement_levers.length > 0 ? (
          <>
            <div className="text-sm text-muted-foreground mb-2">Top 3</div>
            <div className="space-y-3">
              {pillarInsight.top_improvement_levers.map((lever) => (
                <div
                  key={lever.id}
                  className="grid grid-cols-[1fr_2fr_0.5fr_0.5fr] gap-3 text-sm border-b border-border pb-3 last:border-0"
                >
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">
                      Journey Stage
                    </div>
                    <div className="font-medium">
                      {lever.journey_stage || "N/A"}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">
                      Improvement Lever
                    </div>
                    <div className="font-medium">
                      {lever.title || "Untitled"}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">
                      Impact
                    </div>
                    <div className="font-medium">{lever.impact || "N/A"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">
                      Effort
                    </div>
                    <div className="font-medium">{lever.effort || "N/A"}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">
            No improvement levers available yet.
          </p>
        )}
      </CardContent>
      <CardFooter>
        <Link
          href={bizminer_app_pillar_insight_improvement_levers_path(
            pillarInsight.id,
          )}
        >
          <Button className="w-full sm:w-auto">Improvement Levers</Button>
        </Link>
      </CardFooter>
    </Card>
  );
}

function DownloadReportButton({
  url,
  filename,
}: {
  url: string;
  filename: string;
}) {
  return (
    <Button variant="outline" asChild>
      <a href={url} download={filename}>
        <Download className="h-4 w-4 mr-2" />
        Download Report
      </a>
    </Button>
  );
}

export default function Show({ pillar_insight, peer_data }: Props) {
  const IconComponent = getIcon(pillar_insight.icon) || Users;

  return (
    <BizminerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex size-12 items-center justify-center rounded-lg border border-border/50 bg-muted/30">
              <IconComponent className="h-7 w-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {pillar_insight.title}
              </h1>
              <p className="text-muted-foreground">
                {pillar_insight.description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {pillar_insight.report_date && (
              <span className="text-xs text-muted-foreground border border-border rounded-md px-2 py-0.5">
                {pillar_insight.report_date}
              </span>
            )}
            {pillar_insight.docx_report_file && (
              <DownloadReportButton
                url={pillar_insight.docx_report_file.url}
                filename={pillar_insight.docx_report_file.filename}
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ExecutiveSummaryCard
            executiveSummary={pillar_insight.executive_summary}
          />

          <div className="space-y-6">
            <TotalScoreCard
              analysis={pillar_insight.analysis}
              pillarInsight={pillar_insight}
              peerData={peer_data}
            />
            <ImprovementsCard pillarInsight={pillar_insight} />
          </div>
        </div>
      </div>
    </BizminerLayout>
  );
}
